
import { mdiClose, mdiFolderMultipleImage, mdiFolderOutline, mdiHeartOutline, mdiMagnify } from '@mdi/js'
import Icon from '@mdi/react'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputBase from '@mui/material/InputBase'
import AnyCard from 'components/Card'
import EmptyView from 'components/EmptyView'
import FileCard from 'components/FileCard'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import PPSwitch from 'components/PPSwitch'
import {
  ALL_RANGE_FILTER,
  ContentItem,
  GIF_TYPE,
  MyFile,
  PHOTO_TYPE,
  VIDEO_TYPE
} from 'interfaces'
import {
  STOCK_CONTENT_PROVIDER_GIPHY,
  STOCK_CONTENT_PROVIDER_PEXELS,
  STOCK_CONTENT_PROVIDER_PIXABAY,
  STOCK_CONTENT_PROVIDER_UNSPLASH
} from 'interfaces/Content/StockContentProvider'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router'
import { renderLoadingCards } from 'routes/find/routes/home/components/utils'
import FileFolderCarousel from 'routes/find/routes/my-uploads/components/FileFolderCarousel'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap, tap } from 'rxjs/operators'
import { setComposerFile } from 'services/compose'
import { getFavorites } from 'services/content/favorites'
import { selectedContentItemsSelector } from 'services/content/selectors'
import { searchStockContent } from 'services/search/actions'
import { message } from 'services/snackbar'
import { getUploads } from 'services/uploads/actions'
import { myFileFoldersArraySelector } from 'services/uploads/selectors'
import { STOCK_VIDEO_TYPE, ContentType } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import styles from '../TextContentSearchPanel/TextContentSearchPanel.pcss'
import { FetchRandomMedia } from './FetchRandomMedia'
import panelStyles from './MediaSearchPanel.pcss'

const SOURCE_FAVORITES = 'favorites'
const SOURCE_LIBRARY = 'library'
const ALL_FOLDERS_VALUE = 'all'

type MediaSource = typeof SOURCE_FAVORITES
  | typeof SOURCE_LIBRARY
  | typeof STOCK_CONTENT_PROVIDER_PEXELS
  | typeof STOCK_CONTENT_PROVIDER_UNSPLASH
  | typeof STOCK_CONTENT_PROVIDER_PIXABAY
  | typeof STOCK_CONTENT_PROVIDER_GIPHY

const SOURCES_BY_CATEGORY: Record<string, MediaSource[]> = {
  [PHOTO_TYPE]: [
    SOURCE_FAVORITES,
    SOURCE_LIBRARY,
    STOCK_CONTENT_PROVIDER_PEXELS,
    STOCK_CONTENT_PROVIDER_PIXABAY,
    STOCK_CONTENT_PROVIDER_UNSPLASH
  ],
  [VIDEO_TYPE]: [SOURCE_FAVORITES, SOURCE_LIBRARY, STOCK_CONTENT_PROVIDER_PEXELS, STOCK_CONTENT_PROVIDER_PIXABAY],
  [GIF_TYPE]: [SOURCE_FAVORITES, SOURCE_LIBRARY, STOCK_CONTENT_PROVIDER_GIPHY]
}

const DEFAULT_SOURCE_BY_CATEGORY: Record<string, MediaSource> = {
  [PHOTO_TYPE]: STOCK_CONTENT_PROVIDER_PEXELS,
  [VIDEO_TYPE]: STOCK_CONTENT_PROVIDER_PEXELS,
  [GIF_TYPE]: STOCK_CONTENT_PROVIDER_GIPHY
}

const categories = [{
  value: PHOTO_TYPE,
  label: <FormattedMessage id="general.content.filters.photos" />
}, {
  value: VIDEO_TYPE,
  label: <FormattedMessage id="general.content.filters.videos" />
}, {
  value: GIF_TYPE,
  label: <FormattedMessage id="general.content.filters.gifs" />
}]

export type SearchMode = 'favorites' | 'library' | 'stock'

interface MediaSearchPanelProps {
  onClose: () => void
  onVideoUrlAdded: () => void
  mode: SearchMode
  onSearchSubmit?: () => void
}

export function MediaSearchPanel(props: MediaSearchPanelProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const selectedItems = useSelector(selectedContentItemsSelector)
  const fileFolders = useSelector(myFileFoldersArraySelector)
  const [activeCategory, setActiveCategory] = useState<ContentType>(PHOTO_TYPE)
  const [stockProvider, setStockProvider] = useState<MediaSource>(STOCK_CONTENT_PROVIDER_PEXELS)
  const [selectedFolderValue, setSelectedFolderValue] = useState<string>(ALL_FOLDERS_VALUE)
  const [query, setQuery] = useState('')
  const [hasText, setHasText] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const clearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = ''
    }
    setHasText(false)
    setQuery('')
  }
  const [items, setItems] = useState<ContentItem[]>([])
  const [favPhotos, setFavPhotos] = useState<ContentItem[]>([])
  const [favVideos, setFavVideos] = useState<ContentItem[]>([])
  const [favGifs, setFavGifs] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(false)
  const [favLoading, setFavLoading] = useState(false)
  const search$ = useRef<Subject<{ query: string, type: ContentType, source: MediaSource }>>(new Subject())
  const [randomItems, setRandomItems] = useState<any>({
    [PHOTO_TYPE]: {},
    [VIDEO_TYPE]: {},
    [GIF_TYPE]: {}
  })
  const randomItemsFetched = useMemo(() => {
    return Object.values(randomItems).every((item: any) => Object.keys(item).length > 0)
  }, [randomItems])

  const activeSource = useMemo<MediaSource>(() => {
    if (props.mode === 'favorites') return SOURCE_FAVORITES
    if (props.mode === 'library') return SOURCE_LIBRARY
    return stockProvider
  }, [props.mode, stockProvider])

  const stockProviders = useMemo(() => {
    return SOURCES_BY_CATEGORY[activeCategory]
      .filter(s => s !== SOURCE_FAVORITES && s !== SOURCE_LIBRARY)
      .map(s => ({ value: s, label: <FormattedMessage id={`general.content.filters.${s}`} /> }))
  }, [activeCategory])

  const folderOptions: PPSelectOptions = useMemo(() => {
    const opts: PPSelectOptions = {
      [ALL_FOLDERS_VALUE]: { label: 'All', order: 0 }
    }
    fileFolders.forEach((f, idx) => {
      opts[String(f.id)] = { label: f.name, order: idx + 1 }
    })
    return opts
  }, [fileFolders])

  const activeFolder = useMemo(() => {
    if (selectedFolderValue === ALL_FOLDERS_VALUE) return null
    return fileFolders.find(f => String(f.id) === selectedFolderValue) || null
  }, [fileFolders, selectedFolderValue])

  const onFolderSelect = (value: string) => {
    setSelectedFolderValue(value)
  }

  useEffect(() => {
    dispatch(getUploads(true)).unwrap()
  }, [dispatch])

  useEffect(() => {
    const fetchFavorites = async () => {
      setFavLoading(true)
      try {
        const response = await Promise.all([
          dispatch(getFavorites({ page: 0, type: PHOTO_TYPE, range: ALL_RANGE_FILTER })).unwrap(),
          dispatch(getFavorites({ page: 0, type: VIDEO_TYPE, range: ALL_RANGE_FILTER })).unwrap(),
          dispatch(getFavorites({ page: 0, type: GIF_TYPE, range: ALL_RANGE_FILTER })).unwrap()
        ])
        setFavPhotos(response[0].items)
        setFavVideos(response[1].items)
        setFavGifs(response[2].items)
        setFavLoading(false)
      } catch (e) {
        console.log(`Error fetching favorites: ${e}`)
        setFavLoading(false)
      }
    }
    fetchFavorites()
  }, [dispatch])

  useEffect(() => {
    setStockProvider(current => {
      const validProviders = SOURCES_BY_CATEGORY[activeCategory]
        .filter(s => s !== SOURCE_FAVORITES && s !== SOURCE_LIBRARY)
      if (validProviders.includes(current as any)) {
        return current
      }
      return DEFAULT_SOURCE_BY_CATEGORY[activeCategory]
    })
  }, [activeCategory])

  useEffect(() => {
    search$.current = new Subject()
    search$.current
      .pipe(
        tap(() => setLoading(true)),
        mergeMap(({ query, type, source }) => {
          const action = searchStockContent(query, type, source)
          return dispatch(action)
            .pipe(
              catchError(() => {
                dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
                return observableOf({ error: true })
              })
            )
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setItems(current => [...current, ...response])
        }
        setLoading(false)
      })

    return () => {
      search$.current.unsubscribe()
    }
  }, [dispatch, intl])

  useEffect(() => {
    setItems([])
  }, [query, activeSource, activeCategory])

  useEffect(() => {
    const validStockProviders = SOURCES_BY_CATEGORY[activeCategory]
      .filter(s => s !== SOURCE_FAVORITES && s !== SOURCE_LIBRARY)
    if (!validStockProviders.includes(activeSource as any)) {
      return
    }
    const value = query.trim()
    if (value) {
      search$.current?.next({ query: value, type: activeCategory, source: activeSource })
    }
  }, [query, activeCategory, activeSource])

  const onRandomItemsFetched = useCallback((type: ContentType, items: ContentItem[], provider: string) => {
    setRandomItems((current: any) => ({
      ...current,
      [type]: {
        ...current[type],
        [provider]: items
      }
    }))
  }, [])

  const loadingCards = useMemo(() => {
    if ((activeSource === SOURCE_FAVORITES && !favLoading) || !loading || activeSource === SOURCE_LIBRARY) {
      return null
    }

    return renderLoadingCards(undefined, false, true, loading)
  }, [activeSource, favLoading, loading])

  const visibleContentItems = useMemo(() => {
    if (activeSource === SOURCE_FAVORITES) {
      switch (activeCategory) {
        case PHOTO_TYPE:
          return favPhotos
        case VIDEO_TYPE:
          return favVideos
        case GIF_TYPE:
          return favGifs
        default:
          return []
      }
    }

    return items
  }, [activeCategory, activeSource, favGifs, favPhotos, favVideos, items])

  const visibleFiles = useMemo(() => {
    if (activeSource !== SOURCE_LIBRARY || !activeFolder) {
      return []
    }
    const files = activeFolder.files || {}
    return Object.values(files).filter(file => file.type === activeCategory)
  }, [activeCategory, activeSource, activeFolder])

  const createPostFromFile = (file: MyFile) => {
    dispatch(setComposerFile({ url: file.url, name: file.name }))
    if (file.type === VIDEO_TYPE) {
      props.onVideoUrlAdded()
    }
    onMediaAdded(null, false)
  }

  const onMediaAdded = (content: any, aiWriter: boolean) => {
    if (aiWriter) {
      props.onClose()
    } else {
      dispatch(message(intl.formatMessage({ id: 'general.cards.notification.added-to-composer' }), 'info', undefined, true))
    }

    if (content?.type === STOCK_VIDEO_TYPE) {
      props.onVideoUrlAdded()
    }
  }

  const visibleRandomItems = randomItems[activeCategory] ? randomItems[activeCategory][activeSource] || [] : []
  const showRandom = !query && !loading && visibleContentItems.length === 0

  return (
    <div className={`${styles.container} ${panelStyles.container}`} data-testid="media-search-panel">
      <div className={styles.top}>
        <div className={`${styles.nav} ${panelStyles.nav}`}>
          <PPSwitch
            options={categories}
            selectedValue={activeCategory}
            className={styles['contentType-switch']}
            onSelectedValueChange={setActiveCategory as any}
          />
          {props.mode === 'library' && fileFolders.length > 0 && (
            <PPSelect
              icon={<Icon path={mdiFolderOutline} size="20px" />}
              options={folderOptions}
              selectedValue={selectedFolderValue}
              onSelectionChange={onFolderSelect}
              withCaret
              raised
            />
          )}
          {props.mode === 'stock' && stockProviders.length > 1 && (
            <PPSwitch
              options={stockProviders}
              selectedValue={stockProvider as string}
              onSelectedValueChange={setStockProvider as any}
            />
          )}
          {props.mode === 'stock' && (
            <InputBase
              className={panelStyles['search-input']}
              placeholder="Search for media..."
              inputRef={searchInputRef}
              onChange={e => setHasText(Boolean(e.target.value))}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  setQuery((searchInputRef.current?.value || '').trim())
                  props.onSearchSubmit?.()
                }
              }}
              endAdornment={hasText ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" className={panelStyles['clear-btn']} onClick={clearSearch}>
                    <Icon path={mdiClose} size="16px" className={panelStyles['icon-clear']} />
                  </IconButton>
                </InputAdornment>
              ) : null}
            />
          )}
        </div>
      </div>
      <div className={`${styles.content} ${panelStyles.content}`}>
        {activeSource === SOURCE_LIBRARY && selectedFolderValue === ALL_FOLDERS_VALUE && fileFolders.length > 0 && (
          <div>
            {fileFolders.map(folder => (
              <FileFolderCarousel
                key={folder.id}
                folder={folder}
                className={panelStyles['folder-carousel']}
                staticHeader
                filter={activeCategory}
                onSeeAll={setSelectedFolderValue}
                onCardClick={createPostFromFile}
                onCreatePost={createPostFromFile}
              />
            ))}
          </div>
        )}
        <div className={panelStyles.grid}>
          {activeSource !== SOURCE_LIBRARY && visibleContentItems.map(item => (
            <AnyCard
              key={item.id}
              content={item}
              children={item}
              square
              small
              onCompose={onMediaAdded}
            />
          ))}
          {activeSource === SOURCE_LIBRARY && selectedFolderValue !== ALL_FOLDERS_VALUE && visibleFiles.map(file => (
            <FileCard
              key={file.id}
              file={file}
              small
              hideDetails
              className={styles.file}
              selected={Boolean(selectedItems[file.id])}
              onClick={createPostFromFile}
            />
          ))}
          {loadingCards}
          {showRandom && visibleRandomItems.map((item: any) => (
            <AnyCard
              key={item.id}
              content={item}
              children={item}
              square
              small
              onCompose={onMediaAdded}
            />
          ))}
        </div>
        {activeSource === SOURCE_FAVORITES && !favLoading && visibleContentItems.length === 0 && (
          <EmptyView
            title={`No ${activeCategory} favorited yet`}
            icon={mdiHeartOutline}
            subtitle={(
              <div>
                Click ♡ on any content to save it to your favorites.
              </div>
            )}
            carousel
            className={styles.error}
          />
        )}
        {activeSource !== SOURCE_FAVORITES && activeSource !== SOURCE_LIBRARY && !loading && visibleContentItems.length === 0 && query && (
          <EmptyView
            carousel
            icon={mdiMagnify}
            title={`No ${activeCategory} available for that search`}
            subtitle="Please try a different keyword, topic, brand or industry."
            className={styles.error}
          />
        )}
        {showRandom && randomItemsFetched && visibleRandomItems.length === 0
          && activeSource !== SOURCE_FAVORITES && activeSource !== SOURCE_LIBRARY && (
          <EmptyView
            carousel
            icon={mdiMagnify}
            title={`No ${activeCategory} available`}
            subtitle="Try searching for a specific keyword, topic or industry."
            className={styles.error}
          />
        )}
        {activeSource === SOURCE_LIBRARY && Boolean(activeFolder) && visibleFiles.length === 0 && (
          <EmptyView
            title={intl.formatMessage({ id: `uploads.error.carousel-no-${activeCategory}` })}
            carousel
            icon={mdiFolderMultipleImage}
            subtitle={<span>Go to <NavLink to="/content/library">your Library</NavLink> to upload and organize content.</span>}
            className={`${styles.error} ${styles['err-library']}`}
          />
        )}
        {activeSource === SOURCE_LIBRARY && fileFolders.length === 0 && (
          <EmptyView
            title="No content in your library"
            carousel
            icon={mdiFolderMultipleImage}
            subtitle={<span>Go to <NavLink to="/content/library">your Library</NavLink> to upload and organize content.</span>}
            className={styles.error}
          />
        )}
      </div>
      {!randomItemsFetched && <FetchRandomMedia onFetched={onRandomItemsFetched} />}
    </div>
  )
}

export default MediaSearchPanel
